import { useMemberChecklist } from "@circle-react/components/MemberChecklist/hooks/useMemberChecklist";
import { MemberChecklistDrawer } from "@circle-react/components/MemberChecklist/MemberChecklistDrawer";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import {
  chatRoomsPath,
  notificationsFullPagePath,
} from "@circle-react/helpers/urlHelpers";
import { IconButton } from "@circleco/compass/components/IconButton";
import { useEffect, useRef, useState } from "react";

import { t } from "@/i18n-js/instance";

const rootLocale = "header.member_checklist";

const DISABLED_PATHS = [chatRoomsPath(), notificationsFullPagePath()];

interface MobileMemberChecklistFabProps {
  currentPath: string;
}

export const MobileMemberChecklistFab = ({
  currentPath,
}: MobileMemberChecklistFabProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    shouldShowMemberChecklist,
    isMemberChecklistOpen,
    handleMemberChecklistOpenChange,
  } = useMemberChecklist();

  const [isHide, setIsHide] = useState(false);

  const isDisabledPath = DISABLED_PATHS.some(path =>
    currentPath.startsWith(path),
  );
  const isEnabled = shouldShowMemberChecklist && !isDisabledPath;

  useEffect(() => {
    if (!isEnabled) return;

    const footer = containerRef.current?.closest("footer");

    if (!footer) return;

    const observer = new IntersectionObserver(
      ([entry]) => setIsHide(entry.intersectionRatio < 0.9),
      { threshold: [0, 0.9, 1] },
    );
    observer.observe(footer);

    return () => observer.disconnect();
  }, [isEnabled]);

  if (!isEnabled) {
    return null;
  }

  return (
    <>
      <div
        ref={containerRef}
        className={classNames(
          "pointer-events-none absolute inset-x-0 bottom-full flex justify-end px-3 pb-3 transition-opacity duration-100 ease-out",
          isHide ? "opacity-0" : "opacity-100",
        )}
      >
        <div className={isHide ? "pointer-events-none" : "pointer-events-auto"}>
          <IconButton
            icon="to-do-list"
            variant="outline"
            size="lg"
            aria-label={t([rootLocale, "title"])}
            onClick={() =>
              handleMemberChecklistOpenChange(!isMemberChecklistOpen)
            }
          />
        </div>
      </div>

      <MemberChecklistDrawer
        open={isMemberChecklistOpen}
        onOpenChange={handleMemberChecklistOpenChange}
      />
    </>
  );
};
